import { ConfluenceWorkspaceAri, IdentityUserAri } from '@atlassian/ari';

import {
	preloadSimplifiedNavForSpaceNav,
	type OnboardingStateResult,
} from '@confluence/experiment-simplify-nav-new-user/entry-points/preloadSimplifiedNavForSpaceNav.ts';
import { fg } from '@confluence/feature-gating';
import { preloadConnectorOnboardingState } from '@confluence/experiment-twcg-1489-cc-3p-connector-onboarding/entry-points/preloadConnectorOnboardingState.ts';
import { preloadSideNavigationOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadSideNavigationOnboardingState.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { buildInvitedAppShortcutsRecommendationsVariables } from '../PlatformNavigation/AppShortcuts/invitedAppShortcutsRecommendationsVariables.ts';
import InvitedAppShortcutsRecommendationsQuery from '../PlatformNavigation/AppShortcuts/__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';
import type {
	InvitedAppShortcutsRecommendationsQuery$data,
	InvitedAppShortcutsRecommendationsQuery$variables,
} from '../PlatformNavigation/AppShortcuts/__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

import { preloadGlobalApps } from './GlobalItems/AppsItem/preloadGlobalApps.ts';
import GlobalNavigationComponentQuery from './__generated__/GlobalNavigationComponentQuery.graphql.ts';
import type {
	GlobalNavigationComponentQuery$data,
	GlobalNavigationComponentQuery$variables,
} from './__generated__/GlobalNavigationComponentQuery.graphql.ts';

export const preloadGlobalNavigationComponent = async (
	spaceKey?: string,
): Promise<void | any[] | Error[]> => {
	const { cloudId, userId, activationId, accessStatus } = getPreloaderFnContext();

	const promises: Promise<any>[] = [preloadGlobalApps()];

	if (userId && !fg('navx-global-side-nav-confluence')) {
		promises.push(
			queryAgg<GlobalNavigationComponentQuery$data, GlobalNavigationComponentQuery$variables>({
				query: GlobalNavigationComponentQuery,
				context: {
					headers: {
						'Atl-Client-Name': 'confluence-frontend',
					},
				},
				variables: {
					entityAri: ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString(),
					ownerAri: IdentityUserAri.create({ userId }).toString(),
				},
			}),
		);
	}

	// Productionisation experiment (PUMA-2012): preload the GRS invited-app-shortcuts recommendations
	// query for the LEGACY nav via interop (queryAgg → Apollo AGG cache), so the render-time
	// `useQueryInterop` read in `LegacyInvitedAppShortcutsRecommendations` hits the SSR-seeded cache
	// with no second network request. Gated ONLY on the legacy surface (FG off) + the kill-switch gate
	// — NOT the experiment — so BOTH experiment arms (treatment and control) get the store hit. This
	// keeps exposure balanced: the render-time hook fires the exposure for both arms, and control must
	// not incur a client waterfall the treatment arm avoids. GSN preloads this operation separately via
	// platform Relay (`preloadInvitedAppShortcutsRecommendationsQuery`).
	if (
		userId &&
		cloudId &&
		!fg('navx-global-side-nav-confluence') &&
		fg('twcg-460-preload-grs-invited-app-shortcuts-gate')
	) {
		promises.push(
			queryAgg<
				InvitedAppShortcutsRecommendationsQuery$data,
				InvitedAppShortcutsRecommendationsQuery$variables
			>({
				query: InvitedAppShortcutsRecommendationsQuery,
				variables: buildInvitedAppShortcutsRecommendationsVariables(cloudId),
			}),
		);
	}

	if (
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS
	) {
		promises.push(
			preloadSideNavigationOnboardingState(
				// Follow-up cleanup: remove this entire `preloadSideNavigationOnboardingState(...)`
				// call once side-nav no longer needs `NAV_EXPERIMENT_ONBOARDING_KEYS` cache-seeded for
				// xflow. Separately, remove the space-route callback when
				// `cfind_5565_creation_focused_nav_xflow_preload` / `preloadSimplifiedNavForSpaceNav`
				// are no longer needed.
				spaceKey && fg('cfind_5565_creation_focused_nav_xflow_preload')
					? (result) => preloadSimplifiedNavForSpaceNav(spaceKey, result as OnboardingStateResult)
					: undefined,
			),
		);
	}

	if (accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS) {
		promises.push(preloadConnectorOnboardingState(userId ?? undefined));
	}

	return waitForAllReturnWhenError(promises);
};
